/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios'

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete'

interface PaginationParams {
  page?: number
  limit?: number
  withPagination?: string
}

export interface IHttpClient {
  paginate: (page: number, limit: number) => HttpClient
  version: (v: string) => HttpClient
  get: <T>(
    url: string,
    config?: AxiosRequestConfig<any>,
  ) => Promise<AxiosResponse<T, any>>
  post: <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig<any>,
  ) => Promise<AxiosResponse<T, any>>
  put: <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig<any>,
  ) => Promise<AxiosResponse<T, any>>
  patch: <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig<any>,
  ) => Promise<AxiosResponse<T, any>>
  delete: <T>(
    url: string,
    config?: AxiosRequestConfig<any>,
  ) => Promise<AxiosResponse<T, any>>
  addRequestInterceptor(
    onFulfilled: (
      config: InternalAxiosRequestConfig,
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
    onRejected?: (error: any) => any,
  ): number

  addResponseInterceptor(
    onFulfilled: (
      response: AxiosResponse,
    ) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: any) => any,
  ): number

  ejectInterceptor(id: number, type?: 'request' | 'response'): void
}

export class HttpClient implements IHttpClient {
  private instance: AxiosInstance
  private paginationParams: PaginationParams = {}
  private versionPrefix: string = ''
  private reqHeaders: object = {}
  constructor(baseURL: string, config: Omit<CreateAxiosDefaults, 'baseURL'>) {
    this.instance = axios.create({ baseURL, ...config })
  }

  paginate(page: number, limit: number): this {
    this.paginationParams = { page, limit, withPagination: 'yes' }
    return this
  }

  version(v: string): this {
    this.versionPrefix = `/${v.replace(/^\/|\/$/g, '')}`
    return this
  }

  addHeaders(obj: object) {
    this.reqHeaders = { ...this.reqHeaders, ...obj }

    return this
  }

  private buildUrl(url: string): string {
    return `${this.versionPrefix}${url}`
  }

  private isAbsoluteUrl(url: string): boolean {
    return /^(https?:|)\/\//.test(url)
  }

  private async request<T = any>(
    method: HttpMethod,
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    const isAbsolute = this.isAbsoluteUrl(url)
    const finalConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...(config?.headers || {}),
        ...(this.reqHeaders ? this.reqHeaders : {}),
      },
      params: {
        ...(config?.params || {}),
        ...(this.paginationParams.page && { page: this.paginationParams.page }),
        ...(this.paginationParams.limit && {
          limit: this.paginationParams.limit,
        }),
        ...(this.paginationParams.withPagination && {
          withPagination: this.paginationParams.withPagination,
          with_pagination: this.paginationParams.withPagination,
        }),
      },
      ...(isAbsolute ? { baseURL: '' } : {}),
    }

    const fullUrl = isAbsolute ? url : this.buildUrl(url)

    this.resetConfig()

    const response = await this.instance.request<T>({
      method,
      url: fullUrl,
      data,
      ...finalConfig,
    })

    return response
  }

  private resetConfig() {
    this.paginationParams = {}
    this.reqHeaders = {}
    this.versionPrefix = ''
  }

  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>('get', url, undefined, config)
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.request<T>('post', url, data, config)
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.request<T>('put', url, data, config)
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.request<T>('patch', url, data, config)
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>('delete', url, undefined, config)
  }

  addRequestInterceptor(
    onFulfilled: (
      config: InternalAxiosRequestConfig,
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
    onRejected?: (error: any) => any,
  ): number {
    return this.instance.interceptors.request.use(onFulfilled, onRejected)
  }

  addResponseInterceptor(
    onFulfilled: (
      response: AxiosResponse,
    ) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: any) => any,
  ): number {
    return this.instance.interceptors.response.use(onFulfilled, onRejected)
  }

  ejectInterceptor(id: number, type: 'request' | 'response' = 'request') {
    if (type === 'request') {
      this.instance.interceptors.request.eject(id)
    } else {
      this.instance.interceptors.response.eject(id)
    }
  }
}
